import { useDebouncedValue } from '@mantine/hooks'
import { FunctionComponent, MouseEvent, useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useAdQualityRules } from '~/api/ad-quality.api'
import { AdQualityRule } from '@pubstack/common/src/ad-quality-rule'
import { byName } from '@pubstack/common/src/sort'
import { Colors } from '~/assets/style/colors'
import { ActionButtons, ActionsTableCell, NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
// FIXME: Use the GlobalFlyoutWrapper from @pubstack/ui/components
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSearchAndSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { EditAdQualityFlyout } from './EditAdQualityFlyout'
import { ViewAdQualityFlyout } from './ViewAdQualityFlyout'
import { DeleteAdQualityRuleModal } from './DeleteAdQualityRuleModal'
import { useSites } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'

// Constants
const FLYOUT_WIDTH = '600px'
const SEARCH_DEBOUNCE_TIME_MILLIS = 300

// Tracking labels
const CREATE_TRACKING_LABEL = 'settings-ad-quality-new'
const VIEW_TRACKING_LABEL = 'settings-ad-quality-view'
const EDIT_TRACKING_LABEL = 'settings-ad-quality-edit'
const DELETE_TRACKING_LABEL = 'settings-ad-quality-delete'

export const AdQualityPage: FunctionComponent = () => {
  // State
  const [columns, setColumns] = useState<TableColumns<AdQualityRule>>([
    {
      name: 'Rule',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
      sort: (a, b) => {
        //Display scope rule first
        const aWithoutSites = !a.siteIds?.length
        const bWithoutSites = !b.siteIds?.length

        if (aWithoutSites && !bWithoutSites) return -1
        if (!aWithoutSites && bWithoutSites) return 1

        return byName(a, b)
      },
    },
    {
      name: 'Used on',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'siteIds',
      sort: (a, b) => (a.siteIds?.length ?? 0) - (b.siteIds?.length ?? 0),
    },
    {
      name: 'URLs',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'blockedAdvertiserDomains',
      sort: (a, b) => (a.blockedAdvertiserDomains?.length ?? 0) - (b.blockedAdvertiserDomains?.length ?? 0),
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ])

  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()
  const user = useAuthenticatedUser()
  const { data: sites, isLoading: isLoadingSites } = useSites(user.currentScopeId)
  const { data: rules, isLoading } = useAdQualityRules(user.currentScopeId)

  // Form hooks
  const { control } = useForm<{
    search: string
  }>({
    defaultValues: {
      search: '',
    },
  })
  const search = useWatch({ control, name: 'search' })
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_TIME_MILLIS)

  const displayedRules = useMemo(() => {
    const filteredBySearchSortedRules = handleTableSearchAndSort(columns, rules, debouncedSearch, ['name'])
    return filteredBySearchSortedRules
  }, [rules, debouncedSearch, columns])

  const isLoadingPage = isLoading || isLoadingSites

  const scopeRuleId = useMemo(() => rules.find((r) => !r.siteIds?.length)?.id, [rules])

  const handleEditClick = useCallback(
    (event: MouseEvent<HTMLElement>, rule: AdQualityRule) => {
      event.preventDefault()
      event.stopPropagation()
      flyout.open(
        EditAdQualityFlyout,
        { color: Colors.Ming, width: FLYOUT_WIDTH, closeTrackingLabel: `${EDIT_TRACKING_LABEL}-close` },
        { sites, rule, trackingLabel: EDIT_TRACKING_LABEL, scopeRuleId, onClose: () => flyout.close() }
      )
    },
    [flyout, sites, scopeRuleId]
  )

  const handleCreateClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      flyout.open(
        EditAdQualityFlyout,
        { color: Colors.Ming, width: FLYOUT_WIDTH, closeTrackingLabel: `${CREATE_TRACKING_LABEL}-close` },
        { sites, trackingLabel: CREATE_TRACKING_LABEL, scopeRuleId, onClose: () => flyout.close() }
      )
    },
    [flyout, sites, scopeRuleId]
  )

  const handleViewClick = useCallback(
    (event: MouseEvent<HTMLElement>, rule: AdQualityRule) => {
      event.preventDefault()
      event.stopPropagation()
      flyout.open(ViewAdQualityFlyout, { color: Colors.Ming, width: FLYOUT_WIDTH, closeTrackingLabel: `${VIEW_TRACKING_LABEL}-close` }, { rule })
    },
    [flyout]
  )

  const handleDeleteClick = (event: React.MouseEvent<HTMLElement>, rule: AdQualityRule) => {
    event.stopPropagation()
    modal.open(DeleteAdQualityRuleModal, {
      ruleId: rule.id,
      ruleName: rule.name,
      scopeId: user.currentScopeId,
    })
  }

  return (
    <>
      {isLoadingPage || rules.length > 0 ? (
        <>
          <div className="flex w-full items-center justify-between">
            <form className="my-3 flex gap-3">
              <Input name="search" type="text" iconLeft="search" labelIsPlaceholder label="Search" control={control} />
            </form>
            <Button
              variant="primary"
              trackingLabel={CREATE_TRACKING_LABEL}
              onClick={(e: MouseEvent<HTMLElement>) => {
                handleCreateClick(e)
              }}
            >
              New rule
            </Button>
          </div>

          <Table columns={columns} isLoading={isLoadingPage} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
            {displayedRules.length > 0 ? (
              displayedRules.map((rule: AdQualityRule) => (
                <TableRow
                  key={rule.id}
                  className="text-black [&_td]:max-w-87.5 [&_td]:overflow-hidden [&_td]:text-ellipsis"
                  clickable={{
                    onClick: (e: MouseEvent<HTMLElement>) => handleViewClick(e, rule),
                    trackingLabel: VIEW_TRACKING_LABEL,
                  }}
                >
                  <TableCell>{rule.name}</TableCell>
                  <TableCell>{`${rule.siteIds?.length ? rule.siteIds.length + ' media' : 'Scope'}`}</TableCell>
                  <TableCell>{rule.blockedAdvertiserDomains?.length ?? 0}</TableCell>
                  <ActionsTableCell>
                    <ActionButtons>
                      <Tooltip title="Edit">
                        <Button
                          iconName="edit"
                          variant="tertiary"
                          title="Edit"
                          onClick={(e: MouseEvent<HTMLElement>) => {
                            handleEditClick(e, rule)
                          }}
                          trackingLabel={EDIT_TRACKING_LABEL}
                        />
                      </Tooltip>
                      <Tooltip title="View">
                        <Button iconName="view" variant="tertiary" title="View" onClick={(e: MouseEvent<HTMLElement>) => handleViewClick(e, rule)} trackingLabel={VIEW_TRACKING_LABEL} />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button variant="tertiary" iconName="delete" onClick={(e: MouseEvent<HTMLElement>) => handleDeleteClick(e, rule)} trackingLabel={DELETE_TRACKING_LABEL} />
                      </Tooltip>
                    </ActionButtons>
                  </ActionsTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <NoResultsTableCell colspan={columns.length}>No results</NoResultsTableCell>
              </TableRow>
            )}
          </Table>
        </>
      ) : (
        <IllustrationMessage iconName="night_sky" iconSize="190px" title="It seems that no rule exists." message="Add a new rule.">
          <Button
            variant="primary"
            trackingLabel={CREATE_TRACKING_LABEL}
            onClick={(e: MouseEvent<HTMLElement>) => {
              handleCreateClick(e)
            }}
          >
            New rule
          </Button>
        </IllustrationMessage>
      )}
    </>
  )
}
